import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { HeygenAvatarConfig } from "@models/avatar-config";
import { RootState } from "@store/store";
import VideoWrap from "../VideoWrap/VideoWrap";
import Loader from "@components/Loader/Loader";
import { useRecordVoice } from "@hooks/useRecordVoice";
import styled from "@emotion/styled";
import { HeygenAvatarService } from "@service/avatar/heygen-avatar.service";
import {
  setInitializing,
  setProcessing,
  setReady,
  setTalking,
} from "@store/avatarSlice";
import useStreaming from "@components/HeygenAvatar/useStreaming";
import useBatching from "@components/HeygenAvatar/useBatching";
import { EventType } from "@models/audio-message";
import {
  ConversationPacing,
  VoiceInputMode,
} from "@models/conversation-settings";
import { useCompanionConversation } from "@components/Companion/CompanionConversationContext";

const AvatarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
  width: 100%;
  height: 100%;
`;

const HeygenAvatar: FC<{
  sessionId: string;
  photoId?: string;
  voiceContextPhotoId?: string;
  gallerySlideEpoch?: number;
  avatarConfig: HeygenAvatarConfig;
  streaming: boolean;
  conversationPacing: ConversationPacing;
  voiceInputMode: VoiceInputMode;
  pauseDurationMs: number;
  ttsEvictionMs: number;
}> = ({
  sessionId,
  photoId,
  voiceContextPhotoId,
  gallerySlideEpoch,
  avatarConfig,
  streaming,
  conversationPacing,
  voiceInputMode,
  pauseDurationMs,
  ttsEvictionMs,
}) => {
  const avatarRef = useRef<HeygenAvatarService | null>(null);
  const dispatch = useDispatch();
  const processing = useSelector((state: RootState) => state.avatar.processing);
  const initialized = useRef(false);
  // eslint-disable-next-line
  const [running, setRunning] = useState(false); // needed to cause component rerender
  const debouncedPhotoIdRef = useRef<string | undefined>();
  const voiceContextPhotoIdRef = useRef<string | undefined>();
  voiceContextPhotoIdRef.current = voiceContextPhotoId ?? photoId;
  const ttsInterruptGenRef = useRef(0);
  const prevGallerySlideEpochRef = useRef<number | undefined>(undefined);

  const {
    registerPttInterruptHandler,
    cancelPttAutoResume,
    abortPttCapture,
  } = useCompanionConversation();

  const {
    sendMessage: sendMessageStreaming,
    signalPushToTalkEnd,
    signalPushToTalkAbort,
    interruptPlayback,
    suppressGalleryAssistantStream,
  } = useStreaming({
    sessionId,
    avatar: avatarRef,
    ttsEvictionMs,
  });

  const sendMessageBatching = useBatching({
    sessionId,
    avatar: avatarRef,
    ttsInterruptGenerationRef: ttsInterruptGenRef,
    conversationPacing,
  });

  const sendMessage = useCallback(
    (
      audio?: Blob | ArrayBufferLike,
      photoId?: string,
      eventType?: EventType,
    ) => {
      // Blobs only come from MediaRecorder (non-streaming or legacy). MicStream sends ArrayBufferLike → websocket.
      const shouldUseBatching = !streaming || audio instanceof Blob;
      shouldUseBatching
        ? sendMessageBatching(
            audio as Blob,
            photoId,
            eventType,
            conversationPacing,
          )
        : sendMessageStreaming(
            audio as ArrayBufferLike,
            photoId,
            eventType,
            conversationPacing,
          );
    },
    [conversationPacing, sendMessageBatching, sendMessageStreaming, streaming],
  );

  useRecordVoice(
    processing,
    (audio) =>
      sendMessage(audio, voiceContextPhotoIdRef.current, EventType.UserInput),
    streaming,
    voiceInputMode,
    pauseDurationMs,
    () => {
      void signalPushToTalkEnd(
        voiceContextPhotoIdRef.current ?? photoId ?? null,
        conversationPacing,
      );
    },
    () => {
      void signalPushToTalkAbort(
        voiceContextPhotoIdRef.current ?? photoId ?? null,
        conversationPacing,
      );
    },
  );

  useEffect(() => {
    const interruptForPtt = async () => {
      ttsInterruptGenRef.current += 1;
      suppressGalleryAssistantStream();
      await interruptPlayback();
    };
    registerPttInterruptHandler(interruptForPtt);
    return () => {
      registerPttInterruptHandler(null);
    };
  }, [
    interruptPlayback,
    registerPttInterruptHandler,
    suppressGalleryAssistantStream,
  ]);

  const startAvatar = async (avatar: HeygenAvatarService) => {
    dispatch(setInitializing(true));
    await avatar.start(avatarConfig, (talking) => {
      dispatch(setTalking(talking));
      dispatch(setProcessing(talking));
    });

    if (avatar.running && !avatar.stopping) {
      dispatch(setInitializing(false));
      dispatch(setReady(true));
      sendMessage(undefined, photoId, EventType.NewConversation);
    }
    setRunning(true);
  };

  useEffect(() => {
    if (
      photoId &&
      debouncedPhotoIdRef.current &&
      photoId !== debouncedPhotoIdRef.current
    ) {
      sendMessage(undefined, photoId, EventType.NewPhoto);
    }
    debouncedPhotoIdRef.current = photoId;
  }, [photoId, sendMessage]);

  useEffect(() => {
    if (gallerySlideEpoch === undefined) {
      return;
    }
    if (prevGallerySlideEpochRef.current === undefined) {
      prevGallerySlideEpochRef.current = gallerySlideEpoch;
      return;
    }
    if (prevGallerySlideEpochRef.current === gallerySlideEpoch) {
      return;
    }
    prevGallerySlideEpochRef.current = gallerySlideEpoch;
    ttsInterruptGenRef.current += 1;
    suppressGalleryAssistantStream();
    // Slide-induced `talking=false` must not re-arm PTT during the debounced NewPhoto.
    cancelPttAutoResume();
    // If the user was holding "talk" when they slid, abort the in-flight PTT
    // utterance: stop the mic (FAB resets to listen) and tell the server to
    // drop the stale transcript so no out-of-context reply races the intro.
    abortPttCapture();
    void interruptPlayback();
  }, [
    abortPttCapture,
    cancelPttAutoResume,
    gallerySlideEpoch,
    interruptPlayback,
    suppressGalleryAssistantStream,
  ]);

  useEffect(() => {
    if (!initialized.current) {
      dispatch(setProcessing(true));
      initialized.current = true;
      const avatar = new HeygenAvatarService();
      avatarRef.current = avatar;
      startAvatar(avatar);
    }

    return () => {
      if (avatarRef.current?.running && !avatarRef.current.stopping) {
        avatarRef.current.stop();
      }
      dispatch(setReady(false));
      dispatch(setProcessing(false));
    };
  }, [avatarConfig, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!avatarRef.current?.running) {
    return <Loader />;
  }

  return (
    <AvatarContainer>
      <VideoWrap stream={avatarRef.current.stream || undefined} />
    </AvatarContainer>
  );
};

export default memo(HeygenAvatar);
